import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Compact dropdown filter for selecting a location.
 * Renders nothing if there are no locations defined (don't pollute UI).
 *
 * Props:
 *   - locations: list from /api/locations
 *   - users: list to compute per-location counts (optional)
 *   - value: "all" | "_none" | location_id
 *   - onChange: (value) => void
 *   - testid: optional data-testid
 */
export default function LocationFilter({ locations, users = [], value, onChange, testid = "location-filter" }) {
  const { t } = useTranslation();
  if (!locations || locations.length === 0) return null;
  const countAll = users.length;
  const countNone = users.filter(u => !u.location_id).length;
  return (
    <div className="d-flex align-items-center gap-2">
      <i className="bi bi-geo-alt text-primary"></i>
      <select className="form-select form-select-sm" style={{ minWidth: 200 }}
              value={value || "all"} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}>
        <option value="all">{t("employees.location_all")}{countAll ? ` (${countAll})` : ""}</option>
        {countNone > 0 && (
          <option value="_none">{t("employees.location_none")} ({countNone})</option>
        )}
        {locations.map(l => {
          const n = users.filter(u => u.location_id === l.location_id).length;
          return (
            <option key={l.location_id} value={l.location_id}>
              {l.name}{n ? ` (${n})` : ""}
            </option>
          );
        })}
      </select>
    </div>
  );
}

/** Filter a user list by the given filter value. */
export function applyLocationFilter(users, value) {
  if (!value || value === "all") return users;
  if (value === "_none") return users.filter(u => !u.location_id);
  return users.filter(u => u.location_id === value);
}
